import { VARIANT_STAGE_TYPES } from '../constants/stages';

/**
 * CNV filtering for Molecular Impact.
 * Caller computes `isCnvVariant` (via `variant_type` or `copy_number_(loss|gain)` consequences).
 */

export type StageFilterOptions = {
  requireConsequenceForMolecularImpact?: boolean;
  isCnvVariant?: boolean;
};

/**
 * Determines if a variant should be treated as a CNV context based on its type or consequences.
 */
export const checkIsCnvVariant = (
  variantType?: string,
  consequence?: string,
  transcriptConsequences?: string[],
) => {
  const cnvTerms = ['copy_number_loss', 'copy_number_gain'];
  return (
    variantType === 'CNV' ||
    (!!consequence && cnvTerms.includes(consequence)) ||
    (transcriptConsequences?.some((t) => cnvTerms.includes(t)) ?? false)
  );
};

export const filterArgumentsByStage = (
  data: any[],
  targetStage: string,
  variant_consequence?: string,
  options?: StageFilterOptions,
) => {
  const isMolecularImpact =
    targetStage === VARIANT_STAGE_TYPES.MOLECULAR_IMPACT;
  const hasConsequence = !!variant_consequence;

  return data.filter((item) => {
    const isStageMatch = item.stage === targetStage;
    if (!isStageMatch) {
      return false;
    }

    if (!isMolecularImpact) {
      return true;
    }

    // Filter CNV-prefixed arguments only for CNV variants.
    const argumentText = item.text || '';
    const isCnvArgument = argumentText.startsWith('CNV:');
    const isCnvVariant = !!options?.isCnvVariant;
    if (isCnvArgument !== isCnvVariant) {
      return false;
    }

    const consequences = item.variant_transcript_consequences;
    const consequenceMatch =
      hasConsequence &&
      ((Array.isArray(consequences) &&
        consequences.includes(variant_consequence)) ||
        (typeof consequences === 'string' &&
          consequences.includes(variant_consequence as string)));

    const require = options?.requireConsequenceForMolecularImpact;
    if (require) {
      return hasConsequence && consequenceMatch;
    }
    // Default behavior: include all when no consequence provided, otherwise require match
    return !hasConsequence || consequenceMatch;
  });
};
